import { X } from 'lucide-react';
import { useState, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils';

// Dynamically import FullCanvas for real loading
const FullCanvas = lazy(() => import('../FullCanvas'));

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageId?: string;
  image?: string;
  imageTitle?: string;
}

export default function ImageModal({ isOpen, onClose, imageId, imageTitle }: ImageModalProps) {
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [canvasKey, setCanvasKey] = useState(0);

  // Handle client-side mounting for portal
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen && mounted) {
      setIsAnimating(true);
      setShowContent(true);
      // Force remount of canvas when opening with new image
      setCanvasKey(prev => prev + 1);
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
    } else if (!isOpen && showContent) {
      setIsAnimating(false);
      const hideTimer = setTimeout(() => {
        setShowContent(false);
        // Re-enable background scrolling
        document.body.style.overflow = '';
      }, 300);
      return () => clearTimeout(hideTimer);
    }
  }, [isOpen, mounted, showContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!mounted || (!isOpen && !showContent)) return null;

  const modalContent = (
    <div className={cn(
      "fixed inset-0 z-50 transition-opacity duration-300 ease-out overflow-hidden",
      isAnimating ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
    )}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Close button - positioned relative to drawer */}
      {!isMobile && (
        <button
          onClick={onClose}
          className="absolute top-4 right-[calc(85vw+16px)] p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-lg"
          title="Close"
        >
          <X className="h-5 w-5 text-gray-700 dark:text-gray-300" />
        </button>
      )}
      
      {/* Drawer */}
      <div className={cn(
        "absolute bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-300 ease-out overflow-hidden",
        isMobile 
          ? "top-0 left-0 right-0 bottom-0 rounded-t-2xl" 
          : "top-0 right-0 w-[85vw] h-full rounded-l-2xl",
        isAnimating 
          ? "translate-x-0 translate-y-0" 
          : isMobile 
            ? "translate-y-full" 
            : "translate-x-full"
      )}
      style={isMobile ? {
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        height: '100dvh',
        maxHeight: '-webkit-fill-available'
      } : {}}>
        {/* Close button for mobile - inside drawer */}
        {isMobile && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm"
            title="Close"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        )}

        {/* Content - full height with rounded corners */}
        <div className="w-full h-full overflow-hidden rounded-l-2xl">
          <Suspense fallback={
            <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-800 rounded-l-2xl">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400 text-lg">Loading editor...</p>
            </div>
          }>
            <FullCanvas
              key={canvasKey}
              initialImage={imageId ? `https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${imageId}/orig` : undefined}
              initialImageTitle={imageTitle}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}